import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { isNil } from 'lodash';

import { trackByFunctions } from '@assess/browser/track-by-functions';
import { sanitizeFilename } from '@assess/shared/file-constants';

import { getExtensionFromMime, IDuplicateValidationOptions } from './file-upload-types';
import { FormatBytesPipe } from '../../pipes/format-bytes.pipe';
import { FILE_UPLOAD_CONFIG, IFileUploadConfig } from '../../providers/file-upload-config.provider';

@Component({
  selector: 'assess-file-upload',
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: FileUploadComponent, multi: true }],
})
export class FileUploadComponent implements ControlValueAccessor, OnInit, AfterViewInit {
  @ViewChild('file') file!: ElementRef;

  @Input() allowedFileTypes: string[] | null | undefined;
  @Input() maxFileBytes!: number;
  @Input() maxUploads?: number;
  @Input() label = 'Upload Files';
  @Input() duplicateValidation?: IDuplicateValidationOptions;

  @Output() readonly browse = new EventEmitter();
  @Output() init = new EventEmitter<FileUploadComponent>();

  formatBytesPipe = new FormatBytesPipe();
  disabled = false;
  files: File[] = [];
  badFiles: { file: File; reason: string }[] = [];
  readonly trackByIndex = trackByFunctions.byIndex;
  allowedFileExtensions?: string;

  get value(): File[] {
    return this.files;
  }

  constructor(
    private readonly changeDetectorRef: ChangeDetectorRef,
    @Inject(FILE_UPLOAD_CONFIG) private readonly config: IFileUploadConfig
  ) {}

  ngOnInit() {
    this.maxFileBytes = Math.min(this.maxFileBytes || this.config.maxFileBytes, this.config.maxFileBytes);

    if (!this.allowedFileTypes) {
      this.allowedFileTypes = this.config.allowedFileTypes;
    } else {
      this.allowedFileTypes = this.allowedFileTypes.filter((providedFileType) =>
        this.config.allowedFileTypes.includes(providedFileType)
      );
    }

    // Display supported file types logic
    if (this.allowedFileTypes?.length && this.allowedFileTypes?.length <= 10) {
      this.allowedFileExtensions = getExtensionFromMime(this.allowedFileTypes);
    }
  }

  ngAfterViewInit(): void {
    this.init.emit(this);
  }

  @HostListener('dragover', ['$event'])
  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
  }

  @HostListener('dragleave', ['$event'])
  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
  }

  @HostListener('drop', ['$event'])
  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();

    if (!event.dataTransfer?.files.length) {
      return;
    }

    this.onTouched();

    this.addFiles(event.dataTransfer.files);
  }

  onChange = (_: any) => {
    /* nothing */
  };

  onTouched = () => {
    /* nothing */
  };

  onBrowseButtonClick() {
    this.onTouched();

    this.browse.emit();

    this.file.nativeElement.click();
  }

  onFileInputChanged() {
    this.onTouched();

    this.addFiles(this.file.nativeElement.files);
    this.file.nativeElement.value = '';
  }

  isMaxUploads() {
    return !isNil(this.maxUploads) && this.files.length >= this.maxUploads;
  }

  addFiles(filesToAdd: FileList) {
    if (this.disabled || this.isMaxUploads()) {
      return;
    }

    this.badFiles = [];
    const reggies: RegExp[] = this.allowedFileTypes ? this.allowedFileTypes.map((val: string) => new RegExp(val)) : [];
    const unsupportedFileType = `Unsupported file type.`;
    const unsupportedFileSize = `Max file size exceeded. The limit is ${this.formatBytesPipe.transform(
      this.config?.maxFileBytes
    )} bytes.`;

    for (let i = 0; i < filesToAdd.length; i++) {
      const file = filesToAdd.item(i) as File;
      const existingFile = this.files.find((f: File) => {
        return f.name === file.name && f.type === file.type && f.size === file.size;
      });

      if (existingFile) {
        continue;
      }

      // Check for duplicate files across measures if validation is enabled
      if (this.duplicateValidation?.enabled && this.duplicateValidation.duplicateCheckFn) {
        const isDuplicate = this.duplicateValidation.duplicateCheckFn(file);
        if (isDuplicate) {
          this.badFiles.push({
            file,
            reason:
              'This document is already attached to another measure. You do not need to submit the document again for different measures. Our team will review the document and abstract data for all other measures.',
          });
          continue;
        }
      }

      if (this.config && file.size >= this.config.maxFileBytes) {
        this.badFiles.push({
          file,
          reason: unsupportedFileSize + ` "${file.name}" is ${this.formatBytesPipe.transform(file.size)} bytes.`,
        });
        continue;
      }

      if (!reggies.some((reg: RegExp) => reg.test(file.type))) {
        this.badFiles.push({
          file,
          reason:
            unsupportedFileType + ` "${file.name}" is not allowed because it has type "${file.type || 'unknown'}"`,
        });
        continue;
      }

      const { name, type, lastModified } = file;

      this.files.push(new File([file], sanitizeFilename(name), { type, lastModified }));
    }

    this.onChange(this.files);
  }

  onRemoveButtonClick(file: File) {
    const index = this.files.indexOf(file);
    this.files.splice(index, 1);

    this.onTouched();
    this.onChange(this.files);
  }

  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
    this.changeDetectorRef.markForCheck();
  }

  registerOnChange(fn: FileUploadComponent['onChange']) {
    this.onChange = fn;
  }

  registerOnTouched(fn: FileUploadComponent['onTouched']) {
    this.onTouched = fn;
  }

  writeValue(value: FileUploadComponent['files']) {
    this.files = value ?? [];

    this.changeDetectorRef.markForCheck();
  }
}
